import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
    selector: 'app-file-upload-dropzone',
    standalone: true,
    imports: [MatIconModule],
    templateUrl: './file-upload-dropzone.component.html',
    styleUrls: ['./file-upload-dropzone.component.less']
})
export class FileUploadDropzoneComponent {
    @Input() acceptedTypes: string[] = [
        'image/png',
        'image/jpeg',
        'image/gif',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    @Input() maxSizeBytes = 8 * 1024 * 1024;
    @Input() multiple = true;
    @Input() disabled = false;
    @Input() hintText = 'Supported: Images, PDF, Word, Excel';

    @Output() filesSelected = new EventEmitter<File[]>();
    @Output() validationError = new EventEmitter<string>();

    @ViewChild('fileInput') private readonly fileInput!: ElementRef<HTMLInputElement>;

    isDragOver = false;

    @HostListener('dragover', ['$event'])
    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        if (!this.disabled) {
            this.isDragOver = true;
        }
    }

    @HostListener('dragleave', ['$event'])
    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    @HostListener('drop', ['$event'])
    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        if (this.disabled) return;

        const files = event.dataTransfer?.files;
        if (files) {
            this.handleFiles(Array.from(files));
        }
    }

    onFileInputChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files) {
            this.handleFiles(Array.from(input.files));
            input.value = '';
        }
    }

    onContainerClick(): void {
        if (!this.disabled) {
            this.fileInput?.nativeElement.click();
        }
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.onContainerClick();
        }
    }

    getAcceptString(): string {
        return this.acceptedTypes.join(',');
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    private handleFiles(files: File[]): void {
        const validFiles: File[] = [];

        for (const file of files) {
            if (!this.acceptedTypes.includes(file.type)) {
                this.validationError.emit(`File "${file.name}" has an unsupported type.`);
                continue;
            }

            if (file.size > this.maxSizeBytes) {
                const maxSizeMB = this.maxSizeBytes / (1024 * 1024);
                this.validationError.emit(`File "${file.name}" exceeds maximum size of ${maxSizeMB}MB.`);
                continue;
            }

            validFiles.push(file);

            if (!this.multiple) break;
        }

        if (validFiles.length > 0) {
            this.filesSelected.emit(validFiles);
        }
    }
}
